import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, FullscreenTakeover, Step, StepIndicator } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ExitWarningModal } from "../../Common/ExitWarningModal";
import { CaseInformationStep } from "./CaseInformationStep";
import { useEntityClientWizard } from "./context";
import { ProfileStep } from "./ProfileStep";
import { ReviewStep } from "./ReviewStep";
import * as styles from "./styles_vanilla.css";
import { WizardFooterBar } from "./WizardFooterBar";
import type { WizardStep } from "./types";

const STEP_CONFIG: { id: WizardStep; name: string }[] = [
  { id: "case-information", name: "Case information" },
  { id: "profile", name: "Profile" },
  { id: "review", name: "Review" },
];

export const EntityClientCaseWizard = () => {
  const history = useHistory();
  const { currentStep, caseType, footerContent, setRequestExit } = useEntityClientWizard();
  const [showExitWarning, setShowExitWarning] = useState(false);

  const headerTitle =
    caseType === "client"
      ? "Create new client onboarding case"
      : "Create new entity onboarding case";

  const currentStepIndex = useMemo(
    () => STEP_CONFIG.findIndex((s) => s.id === currentStep),
    [currentStep],
  );

  const steps: Step[] = useMemo(
    () =>
      STEP_CONFIG.map((step, index) => {
        let status: "active" | "inactive" | "complete" = "inactive";
        if (index < currentStepIndex) status = "complete";
        else if (index === currentStepIndex) status = "active";
        return { name: step.name, url: step.id, status, hidden: false, index };
      }),
    [currentStepIndex],
  );

  const handleExit = useCallback(() => setShowExitWarning(true), []);

  useEffect(() => {
    setRequestExit(handleExit);
  }, [handleExit, setRequestExit]);

  const handleConfirmExit = useCallback(
    () => history.push(globalEnv.complianceRoute),
    [history],
  );

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case "case-information":
        return <CaseInformationStep />;
      case "profile":
        return <ProfileStep />;
      case "review":
        return <ReviewStep />;
    }
  }, [currentStep]);

  const wizardBodyClassName =
    currentStep === "review" ? styles.wizardBodyReview : styles.wizardBody;

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        title={headerTitle}
        leftActionContent={
          <Button
            variant="muted"
            icon="xmark"
            className={styles.headerExitButton}
            iconPosition="left"
            onClick={handleExit}
            data-testid="exit-button"
            type="button"
          >
            Exit
          </Button>
        }
      />
      <FullscreenTakeover.Subheader>
        <StepIndicator steps={steps} disableStepLinks noPadding />
      </FullscreenTakeover.Subheader>
      <FullscreenTakeover.Body>
        <Flex flexDirection="column" className={wizardBodyClassName}>
          {stepContent}
        </Flex>
      </FullscreenTakeover.Body>
      {footerContent && (
        <FullscreenTakeover.Footer>
          <WizardFooterBar>{footerContent}</WizardFooterBar>
        </FullscreenTakeover.Footer>
      )}
      <ExitWarningModal
        show={showExitWarning}
        onCancel={() => setShowExitWarning(false)}
        onConfirm={handleConfirmExit}
        confirmLabel="Confirm to exit"
      />
    </FullscreenTakeover>
  );
};
